/**
 * ReferralModalParts.js — куски вёрстки модалки «Как это работает».
 *
 * ReferralSteps         — ряд из трёх шагов (регистрация → пробный → бонус)
 * ReferralCodeBox       — код + кнопка «Скопировать» (зелёная после успеха)
 * ReferralRewardCards   — две карточки: бонус вам и бонус коллеге
 * ReferralImportantNote — розовый блок «Важно»
 *
 * isShort — низкий экран: меньше padding, на шагах без второй иконки
 * (номер кружка уже показывает порядок).
 */

import {
  color_black,
  color_black_ultra_light,
  color_green_dark,
  color_grey_light,
  color_grey_ultra_light,
  color_red_default,
  color_red_default_t20,
  color_white,
} from "../../../constants/colors";
import AlertIcon from "../../icons/AlertIcon";
import IconlyCurvedBold3User from "../../icons/IconlyCurvedBold3User";
import IconlyRegularBoldProfile from "../../icons/IconlyRegularBoldProfile";
import {
  OnestBoldDefault,
  OnestBoldSmall,
  OnestNormalSmall,
  OnestSemiBoldDefault,
  OnestSemiBoldMed,
} from "../../styled/TextComponents";
import {
  REFERRAL_IMPORTANT,
  REFERRAL_REWARDS,
  REFERRAL_STEPS,
} from "./referralContent";
import {
  CardIcon,
  CheckIcon,
  CopyIcon,
  GiftOutlineIcon,
  ShareIcon,
} from "./referralIcons";
import { useI18n } from "../../../i18n/I18nProvider";

const STEP_ICONS = {
  share: ShareIcon,
  card: CardIcon,
  gift: GiftOutlineIcon,
};

export const ReferralSteps = ({ isNarrow, isShort }) => {
  const { t } = useI18n();
  return (
  <div
    style={{
      display: "flex",
      flexDirection: isNarrow ? "column" : "row",
      alignItems: isNarrow ? "stretch" : "flex-start",
      gap: isNarrow ? 10 : 0,
    }}
  >
    {REFERRAL_STEPS.map((step, index) => {
      const Icon = STEP_ICONS[step.icon];
      return (
        <div
          key={step.n}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: isNarrow ? "row" : "column",
            alignItems: isNarrow ? "flex-start" : "center",
            textAlign: isNarrow ? "left" : "center",
            gap: isShort ? 4 : isNarrow ? 12 : 8,
            position: "relative",
          }}
        >
          {!isNarrow && index < REFERRAL_STEPS.length - 1 && (
            <div
              style={{
                position: "absolute",
                top: 13,
                left: "calc(50% + 18px)",
                right: "calc(-50% + 18px)",
                height: 1,
                backgroundColor: color_grey_light,
              }}
            />
          )}
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: color_red_default,
              color: color_white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "Onest, sans-serif",
              fontWeight: 700,
              fontSize: 13,
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
            }}
          >
            {step.n}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: isNarrow ? "flex-start" : "center",
              gap: 2,
            }}
          >
            {!isShort && <Icon size={22} />}
            <OnestBoldSmall
              style={{ color: color_black, fontSize: isShort ? 12 : 14 }}
            >
              {t(step.title)}
            </OnestBoldSmall>
            <OnestNormalSmall
              style={{
                color: color_black_ultra_light,
                fontSize: isShort ? 11 : 12,
                lineHeight: 1.3,
              }}
            >
              {t(step.text)}
            </OnestNormalSmall>
          </div>
        </div>
      );
    })}
  </div>
  );
};

export const ReferralCodeBox = ({
  referalCode,
  copied,
  onCopy,
  isNarrow,
  isShort,
}) => {
  const { t } = useI18n();
  return (
  <div
    style={{
      backgroundColor: color_grey_ultra_light,
      borderRadius: 12,
      padding: isShort || isNarrow ? "10px 12px" : "16px 18px",
      display: "flex",
      flexDirection: isNarrow ? "column" : "row",
      alignItems: isNarrow ? "stretch" : "center",
      justifyContent: "space-between",
      gap: 10,
    }}
  >
    <div>
      <OnestNormalSmall style={{ color: color_black_ultra_light, fontSize: 12 }}>
        {t("Ваш реферальный код")}
      </OnestNormalSmall>
      <OnestSemiBoldMed
        style={{
          color: color_black,
          fontSize: isShort ? 20 : 24,
          letterSpacing: 1.2,
          marginTop: 2,
        }}
      >
        {referalCode}
      </OnestSemiBoldMed>
    </div>
    <button
      type="button"
      onClick={onCopy}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        border: "none",
        borderRadius: 12,
        backgroundColor: copied ? color_green_dark : color_red_default,
        color: color_white,
        fontFamily: "Onest, sans-serif",
        fontWeight: 600,
        fontSize: 14,
        padding: isShort ? "8px 14px" : "10px 16px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "background-color 0.2s ease",
      }}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
      {copied ? t("Скопировано") : t("Скопировать")}
    </button>
  </div>
  );
};

const RewardCard = ({ icon, label, reward, rewardHint, footer, isShort }) => {
  const { t } = useI18n();
  return (
  <div
    style={{
      flex: 1,
      border: `1px solid ${color_grey_light}`,
      borderRadius: 12,
      padding: isShort ? 10 : 14,
      display: "flex",
      flexDirection: "column",
      gap: isShort ? 4 : 6,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {icon}
      <OnestSemiBoldDefault style={{ fontSize: isShort ? 14 : 16 }}>
        {t(label)}
      </OnestSemiBoldDefault>
    </div>
    <div>
      <span
        style={{
          color: color_red_default,
          fontFamily: "Onest, sans-serif",
          fontWeight: 700,
          fontSize: isShort ? 18 : 22,
          lineHeight: 1.2,
        }}
      >
        {t(reward)}{" "}
      </span>
      <span
        style={{
          color: color_black,
          fontFamily: "Onest, sans-serif",
          fontWeight: 600,
          fontSize: isShort ? 12 : 14,
        }}
      >
        {t(rewardHint)}
      </span>
    </div>
    <OnestNormalSmall
      style={{
        color: color_black_ultra_light,
        fontSize: isShort ? 11 : 12,
        lineHeight: 1.3,
      }}
    >
      {t(footer)}
    </OnestNormalSmall>
  </div>
  );
};

export const ReferralRewardCards = ({ isNarrow, isShort }) => (
  <div
    style={{
      display: "flex",
      flexDirection: isNarrow ? "column" : "row",
      gap: isShort ? 8 : 12,
    }}
  >
    {REFERRAL_REWARDS.map((card) => (
      <RewardCard
        key={card.id}
        isShort={isShort}
        icon={
          card.id === "you" ? (
            <IconlyRegularBoldProfile fill={color_red_default} />
          ) : (
            <IconlyCurvedBold3User
              width={22}
              height={20}
              fill={color_red_default}
            />
          )
        }
        label={card.label}
        reward={card.reward}
        rewardHint={card.rewardHint}
        footer={card.footer}
      />
    ))}
  </div>
);

export const ReferralImportantNote = ({ isShort }) => {
  const { t } = useI18n();
  return (
  <div
    style={{
      backgroundColor: color_red_default_t20,
      borderRadius: 12,
      padding: isShort ? 10 : 16,
      display: "flex",
      flexDirection: "column",
      gap: isShort ? 4 : 8,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <AlertIcon
        width={isShort ? 16 : 20}
        height={isShort ? 16 : 20}
        fill={color_red_default}
      />
      <OnestBoldDefault
        style={{ color: color_red_default, fontSize: isShort ? 14 : 16 }}
      >
        {t("Важно")}
      </OnestBoldDefault>
    </div>
    {REFERRAL_IMPORTANT.map((line) => (
      <OnestNormalSmall
        key={line}
        style={{
          color: color_black_ultra_light,
          fontSize: isShort ? 11 : 14,
          lineHeight: 1.3,
        }}
      >
        • {t(line)}
      </OnestNormalSmall>
    ))}
  </div>
  );
};
